// Carte territoriale (MapLibre GL JS) : fond vectoriel libre, voisins en retrait, tout ce qui est
// hors du territoire grisé, contour accent, sélecteur d'échelle (Gabon, province, commune,
// arrondissement), marqueurs numérotés identiques à la liste voisine, thème jour ou nuit suivi
// en direct. Sans réseau, le fond manque mais limites et marqueurs restent visibles ; sans WebGL,
// la liste des marqueurs et le sélecteur restent utilisables.
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import type { Map as CarteMapLibre, Marker } from 'maplibre-gl';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { classes } from '../outils/classes';
import { empriseEchelle, numeroMarqueur, type MarqueurCarte, type Position, type Territoire } from './carte/geo';
import { construireStyle, FOND_OPENFREEMAP, lireJetons, type DonneesTerritoire, type FondCarte } from './carte/style';
import styles from './CarteTerritoire.module.css';
import { usePointsRegroupes, type CouchePoints } from './carte/points-regroupes';
import { chargerMapLibre } from './carte/charger-maplibre';

export interface ProprietesCarteTerritoire {
  couchePoints?: CouchePoints;
  titre: string;
  /** Échelles proposées, de la plus large à la plus fine. */
  echelles: Territoire[];
  /** Identifiant de l'échelle affichée. */
  echelle: string;
  onEchelle: (id: string) => void;
  marqueurs?: MarqueurCarte[];
  marqueurActif?: string | null;
  onMarqueur?: (id: string) => void;
  /** Saisie d'une position par un clic sur la carte (dépôt au guichet, signalement). */
  onSaisie?: (position: Position) => void;
  /** Fond de carte ; null pour n'en charger aucun (réseau restreint). OpenFreeMap par défaut. */
  fond?: FondCarte | null;
  hauteur?: number | string;
  /** Tuile du tableau de bord : légende et liste réduites. */
  compact?: boolean;
  className?: string;
  /** Surfaces dessinées sur le territoire (emprises d'autorisations, lot L2) ; polygones WGS84. */
  surfaces?: FeatureCollection<Polygon | MultiPolygon> | null;
  /** Libellés de la légende quand les marqueurs ne sont pas des dossiers (marchés, autorisations). */
  libellesTon?: Partial<Record<MarqueurCarte['ton'], string>>;
  /** Nom des objets marqués, au singulier et au pluriel (« dossier localisé » par défaut). */
  libelleObjets?: { singulier: string; pluriel: string };
}

type Etat = 'chargement' | 'pret' | 'webgl_absent' | 'erreur';
type EtatFond = 'inconnu' | 'ok' | 'indisponible';

function webglDisponible(): boolean {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2'));
  } catch {
    return false;
  }
}

/** Marges du recadrage : le sélecteur d'échelle en haut et la légende en bas ne couvrent pas le territoire. */
const MARGES = { top: 96, right: 32, bottom: 72, left: 32 };

const mouvementReduit = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const LIBELLES_TON: Record<MarqueurCarte['ton'], string> = { ouvert: 'Dossier ouvert', retard: 'En retard', autre: 'Autre dossier' };

export function CarteTerritoire({ titre, echelles, echelle, onEchelle, marqueurs = [], marqueurActif = null, onMarqueur, onSaisie, fond = FOND_OPENFREEMAP, hauteur, compact, className, surfaces = null, libellesTon, libelleObjets, couchePoints }: ProprietesCarteTerritoire) {
  const conteneur = useRef<HTMLDivElement>(null);
  const carte = useRef<CarteMapLibre | null>(null);
  const module = useRef<typeof import('maplibre-gl') | null>(null);
  const poses = useRef<Marker[]>([]);
  const rappels = useRef({ onMarqueur, onSaisie });
  rappels.current = { onMarqueur, onSaisie };
  const [etat, setEtat] = useState<Etat>('chargement');
  usePointsRegroupes(carte, etat === 'pret', couchePoints);
  const [etatFond, setEtatFond] = useState<EtatFond>(fond ? 'inconnu' : 'indisponible');
  const courante = echelles.find((e) => e.id === echelle) ?? echelles[echelles.length - 1];
  // Données du style : échelle courante et surfaces, lues au moment de chaque reconstruction.
  const donneesRef = useRef({ echelle: courante, surfaces });
  donneesRef.current = { echelle: courante, surfaces };
  const signatureFond = JSON.stringify(fond);
  const tons: Record<MarqueurCarte['ton'], string> = { ...LIBELLES_TON, ...libellesTon };
  const objets = libelleObjets ?? { singulier: 'dossier localisé', pluriel: 'dossiers localisés' };

  // Création de la carte (une fois), suivi du thème, destruction au démontage.
  useEffect(() => {
    if (!conteneur.current) return;
    if (!webglDisponible()) {
      setEtat('webgl_absent');
      return;
    }
    let annule = false;
    let observateur: MutationObserver | null = null;
    const media = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const fondCourant = JSON.parse(signatureFond) as FondCarte | null;
    const appliquerTheme = () => {
      const c = carte.current;
      if (c && donneesRef.current.echelle) c.setStyle(construireStyle(lireJetons(), fondCourant, donneesRef.current as DonneesTerritoire), { diff: true });
    };
    void (async () => {
      try {
        const maplibre = await chargerMapLibre();
        await import('maplibre-gl/dist/maplibre-gl.css');
        if (annule || !conteneur.current || !donneesRef.current.echelle) return;
        module.current = maplibre;
        const emprise = empriseEchelle(donneesRef.current.echelle);
        const instance = new maplibre.Map({
          zoomLevelsToOverscale: undefined, // Conserver le rendu de MapLibre 5.
          container: conteneur.current,
          style: construireStyle(lireJetons(), fondCourant, donneesRef.current as DonneesTerritoire),
          bounds: emprise ?? [8.5, -4.2, 14.6, 2.4],
          fitBoundsOptions: { padding: MARGES },
          attributionControl: { compact: true },
          locale: { 'NavigationControl.ZoomIn': 'Zoomer', 'NavigationControl.ZoomOut': 'Dézoomer', 'AttributionControl.ToggleAttribution': 'Afficher les sources' },
        });
        instance.addControl(new maplibre.NavigationControl({ showCompass: false }), 'bottom-right');
        instance.on('load', () => !annule && setEtat('pret'));
        instance.on('error', (e: { sourceId?: string; error?: unknown }) => {
          // Fond injoignable (hors connexion) : état dégradé ; toute autre erreur (style, données) est journalisée.
          if (e.sourceId === 'fond') setEtatFond('indisponible');
          else console.error('Carte du territoire :', e.error ?? e);
        });
        instance.on('sourcedata', (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
          if (e.sourceId === 'fond' && e.isSourceLoaded) setEtatFond((f) => (f === 'indisponible' ? f : 'ok'));
        });
        instance.on('click', (e) => {
          const couches = ['places-unitaires', 'groupes-places'].filter((id) => instance.getLayer(id));
          if (couches.length && instance.queryRenderedFeatures(e.point, { layers: couches }).length) return;
          rappels.current.onSaisie?.([e.lngLat.lng, e.lngLat.lat]);
        });
        carte.current = instance;
        observateur = new MutationObserver(appliquerTheme);
        observateur.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        media?.addEventListener?.('change', appliquerTheme);
      } catch {
        if (!annule) setEtat('erreur');
      }
    })();
    return () => {
      annule = true;
      observateur?.disconnect();
      media?.removeEventListener?.('change', appliquerTheme);
      for (const m of poses.current) m.remove();
      poses.current = [];
      carte.current?.remove();
      carte.current = null;
    };
  }, [signatureFond]);

  // Changement d'échelle : nouveau style (voisins, masque, contour) et recadrage.
  const signatureEchelle = courante ? `${courante.id}|${courante.geometrie ? 'g' : '-'}|${courante.voisins?.length ?? 0}|${courante.differe ? 'd' : ''}` : '';
  useEffect(() => {
    const c = carte.current;
    if (!c || !courante || etat !== 'pret') return;
    c.setStyle(construireStyle(lireJetons(), JSON.parse(signatureFond) as FondCarte | null, { echelle: courante, surfaces: donneesRef.current.surfaces }), { diff: true });
    const emprise = empriseEchelle(courante);
    if (emprise) c.fitBounds(emprise, { padding: MARGES, duration: mouvementReduit() ? 0 : 600 });
    // La signature résume ce qui impose un recadrage ; les autres propriétés de « courante » sont stables.
  }, [signatureEchelle, etat, signatureFond]);

  // Changement des surfaces : la source du style est mise à jour sans recadrage.
  const signatureSurfaces = JSON.stringify(surfaces?.features.map((f) => f.id ?? f.properties) ?? null);
  useEffect(() => {
    const c = carte.current;
    if (!c || etat !== 'pret') return;
    const source = c.getSource('surfaces') as { setData?: (d: FeatureCollection<Polygon | MultiPolygon>) => void } | undefined;
    source?.setData?.(donneesRef.current.surfaces ?? { type: 'FeatureCollection', features: [] });
  }, [signatureSurfaces, etat]);

  // Marqueurs : boutons HTML posés sur la carte, numéro identique à celui de la liste.
  const signatureMarqueurs = JSON.stringify(marqueurs.map((m) => [m.id, m.numero, m.longitude, m.latitude, m.ton])) + marqueurActif;
  useEffect(() => {
    const c = carte.current;
    const maplibre = module.current;
    if (!c || !maplibre || etat !== 'pret') return;
    for (const m of poses.current) m.remove();
    poses.current = marqueurs.map((m) => {
      const bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = classes(styles.marqueur, styles[m.ton], m.id === marqueurActif && styles.actif);
      bouton.textContent = numeroMarqueur(m.numero);
      bouton.setAttribute('aria-label', `${numeroMarqueur(m.numero)} : ${m.libelle} (${tons[m.ton]})`);
      if (m.id === marqueurActif) bouton.setAttribute('aria-pressed', 'true');
      bouton.addEventListener('click', (ev) => {
        ev.stopPropagation();
        rappels.current.onMarqueur?.(m.id);
      });
      return new maplibre.Marker({ element: bouton }).setLngLat([m.longitude, m.latitude]).addTo(c);
    });
  }, [signatureMarqueurs, etat]);

  const sansLimite = courante ? !courante.geometrie && !courante.differe : true;
  const description = courante
    ? `${titre} : ${courante.libelle}${sansLimite ? ', sans limite disponible' : ' mis en avant, le reste grisé'}${marqueurs.length ? `, ${marqueurs.length} ${marqueurs.length > 1 ? objets.pluriel : objets.singulier}` : ''}.`
    : titre;
  const sourcesLimites = [...new Set(echelles.map((e) => e.source).filter(Boolean))].join(' ; ');

  return (
    <figure className={classes(styles.carte, compact && styles.compacte, className)} data-etat={etat} data-fond={etatFond}>
      <div className={styles.cadre} style={{ '--hauteur': typeof hauteur === 'number' ? `${hauteur}px` : (hauteur ?? (compact ? '260px' : '440px')) } as CSSProperties}>
        <div ref={conteneur} className={styles.conteneur} role="region" aria-label={description} />
        <nav aria-label="Échelle de la carte" className={styles.echelles}>
          {echelles.map((e) => (
            <button key={e.id} type="button" aria-pressed={e.id === courante?.id} onClick={() => onEchelle(e.id)} disabled={!e.geometrie && !e.voisins?.length && !e.differe}>
              {e.libelle}
            </button>
          ))}
        </nav>
        {etat === 'webgl_absent' ? <p className={styles.message}>Affichage cartographique indisponible sur cet appareil (WebGL absent). Les dossiers localisés sont listés ci-dessous.</p> : null}
        {etat === 'erreur' ? <p className={styles.message}>La carte n&apos;a pas pu être chargée.</p> : null}
        {etat === 'pret' && etatFond === 'indisponible' ? <p className={styles.message}>Fond de carte indisponible (hors connexion) : seules les limites et les dossiers sont affichés.</p> : null}
        {courante?.indicatif ? <p className={classes(styles.message, styles.indicatif)}>Limites indicatives : aucune source officielle ni libre pour ce découpage.</p> : null}
        {sansLimite && courante ? <p className={classes(styles.message, styles.indicatif)}>Aucune limite officielle ni libre disponible pour {courante.libelle}.</p> : null}
        {!compact ? (
          <ul className={styles.legende} aria-label="Légende">
            {(['ouvert', 'retard', 'autre'] as const).map((ton) => (
              <li key={ton}>
                <span className={classes(styles.puce, styles[ton])} aria-hidden /> {tons[ton]}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <figcaption className={styles.attribution}>
        {fond ? `Fond : ${fond.libelle}. ` : 'Sans fond de carte. '}
        {sourcesLimites ? `Limites : ${sourcesLimites}.` : null}
      </figcaption>
      <ul className={etat === 'webgl_absent' ? styles.liste : 'cc-masque'} aria-label={objets.pluriel.charAt(0).toUpperCase() + objets.pluriel.slice(1)}>
        {marqueurs.map((m) => (
          <li key={m.id}>
            <button type="button" onClick={() => onMarqueur?.(m.id)} aria-pressed={m.id === marqueurActif}>
              <span className="cc-mono">{numeroMarqueur(m.numero)}</span> {m.libelle} ({tons[m.ton]})
            </button>
          </li>
        ))}
      </ul>
    </figure>
  );
}
